import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney, formatDate } from "../lib/format";
import { EstimateTable, Stat, MetaGrid } from "../components/EstimateTable";
import { useAlert } from "../components/Confirm";

export function FunderProfile() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const r = useQuery({ queryKey: ["funder", id], queryFn: () => api.funderProfile(Number(id)) });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const alertModal = useAlert();
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isAdmin = !!me.data?.roles?.admin;

  async function saveEmail() {
    setSaving(true);
    try {
      await api.funderUpdate(Number(id), { contact_email: emailDraft?.trim() || null });
      await qc.invalidateQueries({ queryKey: ["funder", id] });
      setEmailDraft(null);
    } catch (err) {
      await alertModal({ title: "Save failed", body: (err as Error).message, confirmLabel: "OK" });
    } finally {
      setSaving(false);
    }
  }

  if (r.isLoading) return <div className="text-slate-500">Loading...</div>;
  if (!r.data || r.error) return <div className="text-red-600">{(r.error as Error)?.message ?? "Not found"}</div>;

  const f = r.data.funder;
  const k = r.data.kpis ?? {};
  const rag = f.partnership_rag?.toLowerCase();
  const ragBg = rag === "red" ? "bg-liaise-red/15 text-red-700"
              : rag === "amber" ? "bg-liaise-orange/15 text-orange-800"
              : rag === "green" ? "bg-liaise-green/20 text-emerald-800"
              : "bg-slate-100 text-slate-600";

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-liaise-navy">{f.name}</h1>
          <div className="text-sm text-slate-500 mt-1">{f.region ?? "-"} · {f.funder_type ?? "-"}</div>
        </div>
        <Link to="/all-estimates" className="text-sm text-slate-500 hover:underline">← Back</Link>
      </div>

      {f.case_by_case && (
        <div className="rounded-xl bg-liaise-orange/15 text-orange-800 px-4 py-3 text-sm">
          ⚠ Case-by-Case funder.
          {f.case_by_case_deadline && <> Deadline {formatDate(f.case_by_case_deadline)}.</>}
          {f.case_by_case_folder_url && <> <a className="underline" target="_blank" rel="noreferrer" href={f.case_by_case_folder_url}>Open SharePoint folder</a>.</>}
        </div>
      )}

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <MetaGrid items={[
          ["Type", f.funder_type ?? "-"],
          ["Region", f.region ?? "-"],
          ["FY27 uplift %", f.fy27_uplift_pct != null ? `${Number(f.fy27_uplift_pct).toFixed(2)}%` : "-"],
          ["PWS commissioned", String(r.data.pws_count ?? 0)],
          ["Case-by-Case?", f.case_by_case ? "Yes" : "No"],
          ["Dataverse ID", f.dataverse_id ?? <span className="text-slate-400">—</span>],
        ]} />
        {rag && (
          <div className={`mt-4 inline-block text-xs px-2 py-1 rounded-full ${ragBg}`}>
            Partnership RAG: {f.partnership_rag}
          </div>
        )}

        {/* Contact email — admin-editable. Used by the "Send to funder" button on approved estimates. */}
        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Contact email (for sending approved estimates)</div>
          {emailDraft === null ? (
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-liaise-navy flex-1">
                {f.contact_email || <span className="italic text-slate-400">Not set</span>}
              </div>
              {isAdmin && (
                <button onClick={() => setEmailDraft(f.contact_email ?? "")}
                  className="text-xs text-liaise-blue hover:underline">Edit</button>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input type="email" placeholder="e.g. brokerage@norfolk.gov.uk"
                value={emailDraft} onChange={(e) => setEmailDraft(e.target.value)}
                className="flex-1 px-3 py-2 border border-slate-200 rounded text-sm" />
              <button onClick={saveEmail} disabled={saving}
                className="px-3 py-2 rounded bg-liaise-navy text-white text-sm font-medium disabled:opacity-40">
                {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEmailDraft(null)} className="text-xs text-slate-500 hover:underline">Cancel</button>
            </div>
          )}
          <div className="text-xs text-slate-400 mt-1">No automatic sends. Approvers click "Send to funder" on each approved estimate when they're ready.</div>
        </div>
      </section>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Total" value={String(k.total ?? 0)} />
        <Stat label="Drafts" value={String(k.drafts ?? 0)} />
        <Stat label="In review" value={String(k.submitted ?? 0)} highlight={(k.submitted ?? 0) > 0} />
        <Stat label="Approved" value={String(k.approved ?? 0)} />
        <Stat label="Approved £" value={formatMoney(k.approved_value)} />
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 text-sm font-semibold text-liaise-navy">Estimates routed to this funder</div>
        <EstimateTable rows={r.data.estimates ?? []} hide={["funder"]} />
      </section>
    </div>
  );
}
